import React, { useRef, useState } from 'react';
import { approveProposalByEmail, rejectProposalByEmail } from '../services/documentService';
import SignaturePad, { SignaturePadHandle } from './SignaturePad';

interface ProposalActionsProps {
    secretCode?: string;
    encryptedId?: string;
    onSuccess: (action: 'approve' | 'decline', reason?: string) => void;
    disabled?: boolean;
}

const ProposalActions: React.FC<ProposalActionsProps> = ({ secretCode, encryptedId, onSuccess, disabled = false }) => {
    const [loading, setLoading] = useState(false);
    const [showDeclineReason, setShowDeclineReason] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [hasSigned, setHasSigned] = useState(false);
    const signaturePadRef = useRef<SignaturePadHandle>(null);

    const handleApprove = async () => {
        const signaturePad = signaturePadRef.current;
        if (!signaturePad || signaturePad.isEmpty()) {
            setError('Please draw your signature before approving.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let response;
            if (encryptedId) {
                response = await approveProposalByEmail(encryptedId, signaturePad.getSignature());
            } else {
                throw new Error('No valid identifier provided');
            }

            if (response.success) {
                onSuccess('approve');
            } else {
                setError(response.error || 'Failed to approve proposal');
            }
        } catch (err) {
            setError('Failed to submit approval. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDecline = async () => {
        if (!showDeclineReason) {
            setShowDeclineReason(true);
            return;
        }

        if (!declineReason.trim()) {
            setError('Please provide a reason for declining');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let response;
            if (encryptedId) {
                response = await rejectProposalByEmail(encryptedId);
            } else {
                throw new Error('No valid identifier provided');
            }

            if (response.success) {
                onSuccess('decline', declineReason);
            } else {
                setError(response.error || 'Failed to decline proposal');
            }
        } catch (err) {
            setError('Failed to submit decline. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const resetDecline = () => {
        setShowDeclineReason(false);
        setDeclineReason('');
        setError(null);
    };

    return (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-cinch-primary mb-4">Proposal Actions</h3>
            
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{error}</p>
                </div>
            )}

            {!showDeclineReason ? (
                <div className="space-y-3">
                    <div className="mb-2">
                        <SignaturePad ref={signaturePadRef} onChange={setHasSigned} />
                    </div>

                    <button
                        onClick={handleApprove}
                        disabled={loading || disabled || !hasSigned}
                        className="w-full bg-cinch-primary hover:bg-cinch-secondary disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                    >
                        {loading ? (
                            <div className="flex items-center">
                                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                Processing...
                            </div>
                        ) : (
                            <div className="flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Approve Proposal
                            </div>
                        )}
                    </button>
                    
                    <button
                        onClick={handleDecline}
                        disabled={loading || disabled}
                        className="w-full border-2 border-cinch-primary text-cinch-primary hover:bg-red-50 hover:border-red-500 hover:text-red-700 disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                    >
                        <div className="flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Decline Proposal
                        </div>
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    <div>
                        <label htmlFor="decline-reason" className="block text-sm font-medium text-gray-700 mb-2">
                            Please provide a reason for declining (required):
                        </label>
                        <textarea
                            id="decline-reason"
                            value={declineReason}
                            onChange={(e) => setDeclineReason(e.target.value)}
                            rows={4}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cinch-primary focus:border-transparent"
                            placeholder="Enter your reason for declining this proposal..."
                            disabled={loading}
                        />
                    </div>
                    
                    <div className="flex space-x-3">
                        <button
                            onClick={handleDecline}
                            disabled={loading || !declineReason.trim()}
                            className="flex-1 border-2 border-cinch-primary text-cinch-primary hover:bg-red-50 hover:border-red-500 hover:text-red-700 disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                        >
                            {loading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                    Submitting...
                                </div>
                            ) : (
                                'Submit Decline'
                            )}
                        </button>
                        
                        <button
                            onClick={resetDecline}
                            disabled={loading}
                            className="flex-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                    Once you submit your response, it cannot be changed.
                </p>
            </div>
        </div>
    );
};

export default ProposalActions;